import { useState } from "react";
import { PageHeader } from "@/components/ui/page-header";
import { ActivityTimeline } from "@/components/ui/activity-timeline";
import { useGlobalTimeline } from "@/hooks/useTimeline";
import { cn } from "@/lib/utils";
import type { TimelineEntry } from "@/types/crm";

export type ActivityFilter = "all" | "updates" | "notes" | "created";

const FILTERS: { id: ActivityFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "updates", label: "Updates" },
  { id: "notes", label: "Notes" },
  { id: "created", label: "Created" },
];

const MATCH: Record<ActivityFilter, (entry: TimelineEntry) => boolean> = {
  all: () => true,
  updates: (entry) => entry.action === "updated",
  notes: (entry) => entry.action === "note",
  created: (entry) => entry.action === "created",
};

export function ActivityFeed() {
  const timeline = useGlobalTimeline(30);
  const [filter, setFilter] = useState<ActivityFilter>("all");
  const entries = timeline.entries.filter(MATCH[filter]);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <PageHeader
        title="Activity"
        description="Everything that changed across your workspace"
        askAi
      />

      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter activity">
        {FILTERS.map((option) => (
          <button
            key={option.id}
            type="button"
            aria-pressed={filter === option.id}
            onClick={() => setFilter(option.id)}
            className={cn(
              "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
              filter === option.id
                ? "border-foreground bg-foreground text-background"
                : "bg-card text-muted-foreground hover:text-foreground",
            )}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="rounded-[10px] border bg-card px-4 pb-3 pt-2">
        <ActivityTimeline
          entries={entries}
          isLoading={timeline.isLoading}
          hasMore={timeline.hasMore}
          onLoadMore={() => timeline.loadMore()}
          isLoadingMore={timeline.isLoadingMore}
        />
      </div>
    </div>
  );
}
